import { Arr, NullFunction, VoidCallback1 } from '@deqo/foundation';
import { Component, ComponentType, Convention, ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Resolver } from '../../Container';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { RootOverride } from '../RootOverride';
import { Selection, SelectionSet } from '../Selection';
import { Physical, RelativeTo } from './Physical';
import { Resizable } from './Resizable';
import { Root } from './Root';

const all = (components: Component[], ...typeOrConv: (ComponentType | Convention<Component>)[]) => {
    return !components.find(c => !typeOrConv.find(t => c.is(t)));
};

const any = <T extends Component>(components: Component[], typeOrConv: ComponentType<T> | Convention<T>) => {
    return !!components.find(x => x.is(typeOrConv));
};

const bounds = (subjects: Physical[]) => Rect.fromMany(subjects.filter(x => x.alive).map(x => x.bounds(RelativeTo.Model)));

const createAtCameraPosition = <T extends Physical>(r: Resolver, type: ComponentType<T>, configurator?: VoidCallback1<T>) => {

    const camera = r.call(Camera);
    const objects = r.call(ObjectManager);
    const session = r.call(EditorSession);

    const root = session.get(RootOverride) ?? objects.get('root', Root);
    const ncp = root.toRelative(camera.position.round(0)).round();

    return objects.update(u => {

        const nc = u.create(type);
        if (configurator) configurator(nc);
        nc.moveTo(ncp.subtract(nc.size().divide(2)));
        root.add(nc);

        session.update(Selection, SelectionSet.create([nc]));

        return nc;
    });

};

type CreateDimensions = readonly [(number | 'auto')?, (number | 'auto')?, number?, number?];

const create = <T extends Physical>(r: Resolver, type: ComponentType<T>, at?: CreateDimensions, configurator?: VoidCallback1<T>): T => {
    at = at ?? [];
    configurator = configurator ?? NullFunction;

    const camera = r.call(Camera);
    const objects = r.call(ObjectManager);
    const session = r.call(EditorSession);

    const root = session.get(RootOverride) ?? objects.get('root', Root);
    const auto = root.toRelative(camera.position.round(0)).round();

    const cc = (c: any) => !!c && c !== 'auto';
    const rc = (a: any, b) => cc(a) ? a : b;

    const ncp = new Point(rc(at[0], auto.x), rc(at[1], auto.y));
    const ncs = cc(at[2]) && cc(at[3]) ? new Point(at[2], at[3]) : null;

    return objects.update(u => {

        const nc = u.create(type);
        configurator(nc);

        if (ncs && nc.is(Resizable)) {
            nc.as(Resizable).resize(ncs.w, ncs.h);
        }
        if (ncp && nc.is(Physical)) {
            nc.as(Physical).moveTo(ncp.subtract(nc.size().divide(2)));
        }

        root.add(nc);

        session.update(Selection, SelectionSet.create([nc]));

        return nc;
    });

};

interface Polarize {
    <T extends Component>(set: Iterable<Component>, ct: ComponentType<T>): T[];
    <T>(set: Iterable<Component>, c: Convention<T>): T[];
}

const polarize: Polarize = (set: any, toc: any) => {
    const ts = [] as any[];
    for (const c of set) {
        if (c.is(toc)) {
            ts.push(c);
        }
    }
    return ts;
};

const highestOf = (components: Iterable<Physical>) => {
    return Arr.last(topToBottom(components));
};

const above = (components: Physical[], target: Physical) => {
    return components
};

const isAbove = (a: Component, b: Component) => {
    return sorter(a, b) > 0;
};

const isAboveAny = (subject: Component, targets: Component[]) => {
    return targets.every(t => isAbove(subject, t));
};

const isBelow = (a: Component, b: Component) => {
    return sorter(a, b) < 0;
};

const isBelowAny = (subject: Component, targets: Component[]) => {
    return targets.every(t => isBelow(subject, t));
};

const below = (components: Iterable<Physical>, target: Physical) => {

};

const sorter = (a: Component, b: Component) => {
    while (true) {
        if (a.parent === b) return 1;
        if (b.parent === a) return -1;
        if (a.parent === b.parent) return a.order - b.order;
        const ad = a.depth();
        const bd = b.depth();
        if (ad > bd || ad === bd) a = a.parent;
        if (bd > ad || bd === ad) b = b.parent;
    }
};

const topToBottom = <T extends Physical = Physical>(components: Iterable<T>): T[] => {
    return Array.from(components).sort(sorter);
};

const bottomToTop = <T extends Physical = Physical>(components: Iterable<T>): T[] => {
    return Array.from(components).sort((a, b) => sorter(a, b) * -1);
};

const top = <T extends Component>(components: Iterable<T>, shouldSort?: boolean) => {
    const set = components instanceof Set ? components : new Set(components);
    const results = [] as T[];
    for (const p of components) {
        if (!p.ancestors().find(a => set.has(a))) {
            results.push(p);
        }
    }
    if (shouldSort) {
        results.sort(sorter);
    }
    return results;
};

const bottom = <T extends Component>(components: Iterable<T>) => {
    const set = components instanceof Set ? components : new Set(components);
    for (const p of components) {
        for (const a of p.ancestors()) {
            set.delete(a);
        }
    }
    return Array.from(set);
};

const nearestTo = <T extends Physical>(candidates: Iterable<T>, target: Physical | Point): T => {
    const pt = target instanceof Point ? target : target.center(RelativeTo.Model);

    let nearDist = Number.MAX_SAFE_INTEGER;
    let nearObj = null as T;

    for (const obj of candidates) {
        const dist = obj.center(RelativeTo.Model).distanceTo(pt);
        if (nearDist > dist) {
            nearDist = dist;
            nearObj = obj;
        }
    }

    return nearObj;
};

const farthestFrom = <T extends Physical>(candidates: Iterable<T>, target: Physical | Point): T => {
    const pt = target instanceof Point ? target : target.center(RelativeTo.Model);

    let farDist = Number.MIN_SAFE_INTEGER;
    let farObj = null as T;

    for (const obj of candidates) {
        const dist = obj.center(RelativeTo.Model).distanceTo(pt);
        if (farDist < dist) {
            farDist = dist;
            farObj = obj;
        }
    }

    return farObj;
};

export const uniformType = (physicals: Iterable<Physical>) => {
    let type = null;
    for (const p of physicals) {
        if (!!type && p.type !== type) return false;
        type = p.type;
    }
    return true;
};

export const uniformSize = (physicals: Iterable<Physical>) => {
    let s = null as Point;
    for (const p of physicals) {
        if (!!s && !s.equals(p.size())) return false;
        s = p.size();
    }
    return true;
};

export const uniform = (physicals: Iterable<Physical>) => uniformType(physicals) && uniformSize(physicals);

export const Physicals = Object.freeze({
    all,
    any,
    bottom,
    bounds,
    create,
    createAtCameraPosition,
    highestOf,
    isAbove,
    isAboveAny,
    isBelow,
    isBelowAny,
    nearestTo,
    farthestFrom,
    polarize,
    bottomToTop,
    topToBottom,
    sorter,
    top,
    uniform,
    uniformSize,
    uniformType,
});

